// SettingsDimensionInputs.jsx
import React from "react";
import CustomSelect from "../../../Common/CustomSelect";
import { ORIENTATION, normalizeOrientation } from "../../../../builders/layoutMode";
import { SettingGroup, SettingField, NumberInput, ExampleBlock } from "./SettingField";
import { DuctsDiagram, ToleranceDiagram, FillSpaceDiagram, MinDistanceDiagram } from "./SettingsDiagrams";
import "./SettingsDimensionInputs.css";

/**
 * Ducts + dimension settings, used by every orientation.
 * (The Space Oriented options now live in SpaceOptionsInputs and are only
 * shown when Space Oriented is selected.)
 *
 * Props:
 *  orientation                      0/1/2 or enum name - which settings are used
 *  ductOptions                      [{ value, label }] – first entry may be "None"
 *  selectedVerticalDuctRefId / onVerticalDuctRefChange(value)
 *  selectedTopDuctRefId / onTopDuctRefChange(value)
 *  toleranceSection / onToleranceSectionChange(event)
 *  fillMountingSpaceCabinet / onFillMountingSpaceChange(event)
 *  minDistanceWireDuctCabinet / onMinDistanceWireDuctChange(event)
 *
 * The three number handlers still receive an event-like { target: { value } },
 * so ScenarioSettingsPanel's existing handlers keep working unchanged.
 */

const asEvent = (handler) => (value) => handler?.({ target: { value } });

const SettingsDimensionInputs = ({
  orientation,
  ductOptions = [],

  selectedVerticalDuctRefId,
  onVerticalDuctRefChange,

  selectedTopDuctRefId,
  onTopDuctRefChange,

  toleranceSection,
  onToleranceSectionChange,

  fillMountingSpaceCabinet,
  onFillMountingSpaceChange,

  minDistanceWireDuctCabinet,
  onMinDistanceWireDuctChange,
}) => {
  const mode = normalizeOrientation(orientation);
  const isSection = mode === ORIENTATION.SECTION;
  const isSpace = mode === ORIENTATION.SPACE;

  const options = ductOptions.length
    ? ductOptions
    : [{ value: "", label: "No ducts available", disabled: true }];

  const fill = Number(fillMountingSpaceCabinet ?? 0);
  const tolerance = Number(toleranceSection ?? 0);
  const plannedOf600 = Math.round(600 * (1 - tolerance / 100));

  return (
    <div className="dim-inputs">
      {/* ── Ducts ─────────────────────────────────────────────────────── */}
      <SettingGroup
        title="Ducts"
        intro="Which cable ducts are drawn and how much space they take. Used in every orientation."
      >
        <SettingField
          id="ssp-duct-enclosure"
          label="Enclosure ducts"
          stacked
          hint="The ducts on the left, right and top edge of the mounting plate."
          example={
            <ExampleBlock diagram={<DuctsDiagram />}>
              The <b>enclosure duct</b> frames the mounting plate; the <b>section duct</b>
              runs under every row of devices. Their widths come from the selected duct
              articles. With <i>– None –</i> a placeholder duct is drawn in a light colour,
              so you can still judge the layout before choosing real ducts.
            </ExampleBlock>
          }
        >
          <CustomSelect
            label={null}
            value={selectedVerticalDuctRefId ?? ""}
            onChange={onVerticalDuctRefChange}
            options={options}
          />
        </SettingField>

        <SettingField
          id="ssp-duct-section"
          label="Section ducts"
          stacked
          hint="The horizontal ducts under every row of devices, and between rows when a section is split."
        >
          <CustomSelect
            label={null}
            value={selectedTopDuctRefId ?? ""}
            onChange={onTopDuctRefChange}
            options={options}
          />
        </SettingField>
      </SettingGroup>

      {/* ── Dimensions ────────────────────────────────────────────────── */}
      <SettingGroup
        title="Space and distances"
        intro="How much of the mounting plate is used, and how close things may get."
      >
        <SettingField
          id="ssp-tolerance"
          label="Expansion reserve"
          hint="Part of every row kept free for later extensions."
          example={
            <ExampleBlock diagram={<ToleranceDiagram />}>
              600&nbsp;mm between the side ducts and <b>{tolerance}&nbsp;%</b>: rows are
              filled up to {plannedOf600}&nbsp;mm, {600 - plannedOf600}&nbsp;mm stay free in
              every row. Typical values are 10–20&nbsp;%.
            </ExampleBlock>
          }
        >
          <NumberInput
            id="ssp-tolerance"
            value={toleranceSection ?? 0}
            min={0}
            max={90}
            unit="%"
            onChange={asEvent(onToleranceSectionChange)}
          />
        </SettingField>

        <SettingField
          id="ssp-fill"
          label="Fill free space from"
          inactiveNote={isSection ? "Not used in Section Mode (every section starts a new row)." : null}
          hint={
            isSpace
              ? "Smallest free width at the end of a row that the next section may fill."
              : "Smallest free width next to a section that the next section may use."
          }
          example={
            <ExampleBlock diagram={<FillSpaceDiagram />}>
              With <b>{fill}&nbsp;mm</b>: a gap of {Math.max(0, fill - 50)}&nbsp;mm stays empty,
              a gap of {fill + 50}&nbsp;mm is used by the next section. Smaller values fill
              more of the plate; larger values avoid small, crowded leftovers.
            </ExampleBlock>
          }
        >
          <NumberInput
            id="ssp-fill"
            value={fillMountingSpaceCabinet ?? 0}
            min={0}
            unit="mm"
            onChange={asEvent(onFillMountingSpaceChange)}
          />
        </SettingField>

        <SettingField
          id="ssp-min-distance"
          label="Distance to wire duct"
          inactiveNote={isSection ? "Not used in Section Oriented Mode." : null}
          hint={
            isSpace
              ? "Free space a duct needs below a row inside a filled-in gap; without it, no duct is added there."
              : "Extra space between rows of a section, in addition to the duct between them."
          }
          example={
            <ExampleBlock diagram={<MinDistanceDiagram />}>
              {isSpace ? (
                <>
                  With <b>{minDistanceWireDuctCabinet ?? 0}&nbsp;mm</b>: a duct between two rows
                  in a filled-in gap is only drawn if the duct plus this distance still fits
                  below the upper row.
                </>
              ) : (
                <>
                  A 40&nbsp;mm duct and <b>{minDistanceWireDuctCabinet ?? 0}&nbsp;mm</b>: wrapped
                  rows are {40 + Number(minDistanceWireDuctCabinet ?? 0)}&nbsp;mm apart, so
                  wires can be routed into the duct comfortably.
                </>
              )}
            </ExampleBlock>
          }
        >
          <NumberInput
            id="ssp-min-distance"
            value={minDistanceWireDuctCabinet ?? 0}
            min={0}
            unit="mm"
            onChange={asEvent(onMinDistanceWireDuctChange)}
          />
        </SettingField>
      </SettingGroup>
    </div>
  );
};

export default SettingsDimensionInputs;
